import { useState, useEffect, useContext } from "react";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import axios from "axios";
import { useNavigate, ViewContext } from "components/lib";
import { ModalContext } from "app/app";

// Components
import { IconedButton } from "components/IconedButton";
import ResponsiveTable from "components/ResponsiveTable";
import CustomLoader from "components/CustomLoader";

// Helpers
import DateUtils from "helpers/dateUtils";
import InputUtils from "helpers/inputUtils";
import { endorsementColumns } from "./columns";

const inputUtils = new InputUtils();
const dateUtils = new DateUtils();

export function EndorsementsV2() {
  const navigate = useNavigate();
  const context = useContext(ViewContext);
  const { setModal } = useContext(ModalContext);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [rowSelection, setRowSelection] = useState({});
  const [columnFilters, setColumnFilters] = useState([]);
  const [activeTab, setActiveTab] = useState("active");
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });

  const tabs = [
    { id: "active", label: "Active" },
    { id: "bound", label: "Bound" },
    { id: "declined", label: "Declined" },
    { id: "not_taken_up", label: "Not Taken Up" },
    { id: "permanently_delete_ticket", label: "Deleted" },
  ];

  const listEndorsements = async (
    pageIndex,
    pageSize,
    sortBy,
    sortOrder,
    search,
    status
  ) => {
    try {
      setLoading(true);

      const response = await axios.get('/api/endorsement/admin', {
        params: {
          page: pageIndex + 1,
          pageSize,
          search,
          sortBy,
          sortOrder,
          status: status !== "all" ? status : undefined
        }
      });

      if (response.data.data) {
        const formattedData = response.data.data.data.map(value => ({
          id: value.id,
          endorsement_number: value.endorsement_number || 'N/A',
          Company: inputUtils.capitalizeFirstLetter(value.company_name).replaceAll('Llc', 'LLC'),
          agency: value.account_name || 'N/A',
          Submission_Number: value.submission_number,
          createdAt: dateUtils.timestamp(value.createdAt),
          status: value.status.replaceAll('_', ' '),
          quote_request: value.quote_request,
          effective_date: dateUtils.isoDate(value.effective_date),
          handleView: (row) => {
            navigate(`/admin/endorsement/${row.id}`, {
              state: {
                data: {
                  id: row.id,
                  submission_number: row.Submission_Number,
                  company: row.Company,
                  agency: row.agency,
                  changes: row.changes,
                },
              },
            });
          },
        }));

        setData(formattedData);
        setPagination(prev => ({
          ...prev,
          totalItems: response.data.data.rows,
          totalPages: response.data.data.pages_count,
        }));
      }
    } catch (error) {
      context.notification.show(
        error.message || "Error fetching endorsements",
        "error",
        true,
        "toast",
        "x-circle"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let currentSortField = 'createdAt';
    let currentSortDirection = 'desc';

    const status = activeTab;

    listEndorsements(
      pagination.pageIndex,
      pagination.pageSize,
      currentSortField,
      currentSortDirection,
      globalFilter,
      status
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    sorting,
    globalFilter,
    columnFilters,
    activeTab // Add activeTab to dependencies
  ]);

  const table = useReactTable({
    data,
    columns: endorsementColumns,
    state: {
      sorting,
      globalFilter,
      columnFilters,
      rowSelection,
      pagination,
    },
    initialState: {
      pagination: {
        pageIndex: pagination.pageIndex,
        pageSize: pagination.pageSize,
      },
    },
    // Pagination
    onPaginationChange: setPagination,
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    // Column
    onColumnFiltersChange: setColumnFilters,
    // Row
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: true,
    // Sort and Filter
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  });

  return (
    <div>
      <div className="flex w-full items-center justify-end mb-4">
        <IconedButton
          size="210px"
          color="#d32425"
          textColor="white"
          icon="plus-circle"
          text="Request Endorsement"
          marginRight
          action={() =>
            setModal({
              name: "endorsement_modal_v2",
              action: () =>
                listEndorsements(
                  pagination.pageIndex,
                  pagination.pageSize,
                  sorting[0]?.id || "createdAt",
                  sorting[0]?.desc ? "desc" : "asc",
                  globalFilter
                ),
            })
          }
        />
      </div>

      {loading && data.length === 0 ? (
        <div className="flex items-center justify-center h-96">
          <CustomLoader />
        </div>
      ) : (
        <ResponsiveTable
          table={table}
          globalFilter={globalFilter}
          setGlobalFilter={setGlobalFilter}
          loading={loading}
          hasFooter={true}
          totalPages={pagination.totalPages}
          totalItems={pagination.totalItems}
          hasTabs={true}
          tabs={tabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      )}
    </div>
  );
}